// Define the base URL for your Go backend
// const BACKEND_BASE_URL: string = 'http://localhost:3000/api/s3'; // Adjust if your backend runs on a different port or path
const BACKEND_BASE_URL: string = 'https://databrix.ebosrndportal.com/api/s3'; // Adjust if your backend runs on a different port or path

// Interface for S3 bucket data (simplified as the backend now returns this structure)
export interface Bucket {
  Name?: string;
  CreationDate?: Date; // You might need to adjust this based on how your Go backend serializes dates
}

// Interface for S3 object items (simplified as the backend now returns this structure)
export interface S3ObjectItem {
  Key?: string;
  Size?: number;
  LastModified?: Date; // You might need to adjust this based on how your Go backend serializes dates
}

/**
 * Lists available S3 buckets by calling the backend proxy.
 * @returns List of S3 buckets
 */
export const listBuckets = async (): Promise<Bucket[]> => {
  try {
    console.log('Listing S3 buckets via backend proxy...');
    const response = await fetch(`${BACKEND_BASE_URL}/buckets`);

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
    }

    const buckets: Bucket[] = await response.json();
    console.log(`Found ${buckets.length} buckets via backend`);

    return buckets;
  } catch (error) {
    console.error('Error listing S3 buckets:', error);
    // In case of an error, return sample data for development as a fallback
    console.log('Error occurred, returning sample buckets for development');
    return [
      { Name: 'dataspace1', CreationDate: new Date() },
      { Name: 'dataspace2', CreationDate: new Date() },
    ];
  }
};

/**
 * Lists objects in an S3 bucket with a given prefix by calling the backend proxy.
 * @param prefix Prefix to filter objects (virtual folder)
 * @returns List of S3 objects
 */
export const listS3Objects = async (bucketName: string, prefix: string = ''): Promise<S3ObjectItem[]> => {
  try {
    console.log(`Listing S3 objects from bucket ${bucketName} with prefix: ${prefix} via backend proxy`);
    // Note: Your backend's ListObjects handler doesn't currently take a bucket name
    // It uses the S3_BUCKET_NAME from its environment. You might want to update
    // your Go backend to accept a bucket name as a query parameter if you need
    // to list objects from different buckets from the frontend.
    const url = new URL(`${BACKEND_BASE_URL}/objects`);
    url.searchParams.append('prefix', prefix);

    const response = await fetch(url.toString());

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
    }

    const objects: S3ObjectItem[] = await response.json();
    console.log(`Found ${objects.length} objects via backend`);

    // For development, if no files are found, add sample files
    if (objects.length === 0) {
      console.log('No files found from backend, adding sample files for development');
      return [
        { Key: 'sample-data-backend.csv', Size: 1024, LastModified: new Date() },
        { Key: 'example-backend.json', Size: 2048, LastModified: new Date() },
      ];
    }

    return objects;
  } catch (error) {
    console.error('Error listing S3 objects:', error);
    // In case of an error, return sample files for development
    console.log('Error occurred, adding sample files for development');
    return [
      { Key: 'sample-data-error.csv', Size: 1024, LastModified: new Date() },
      { Key: 'example-error.json', Size: 2048, LastModified: new Date() },
    ];
  }
};

/**
 * Retrieves an S3 object by its key by calling the backend proxy.
 * The backend will stream the object content.
 * @param key Key of the S3 object
 * @returns Raw content of the S3 object as a Blob (or ArrayBuffer/Response if preferred)
 */
export const getS3Object = async (key: string): Promise<Blob> => {
  try {
    console.log(`Getting S3 object: ${key} via backend proxy`);

    const response = await fetch(`${BACKEND_BASE_URL}/object`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ key }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
    }

    // The backend sends raw binary data, so we can directly get it as a Blob
    const data = await response.blob();
    console.log(`Successfully retrieved S3 object: ${key} via backend`);

    return data;
  } catch (error) {
    console.error(`Error getting S3 object ${key}:`, error);
    throw new Error(`Failed to get S3 object: ${error instanceof Error ? error.message : String(error)}`);
  }
};

/**
 * Retrieves and parses a JSON object from S3 via the backend proxy.
 * @param key Key of the S3 object
 * @returns Parsed JSON data
 */
export const getJsonFromS3 = async <T>(key: string): Promise<T> => {
  try {
    const objectBlob = await getS3Object(key);

    if (!objectBlob) {
      throw new Error('S3 object body is empty or not retrieved');
    }

    const jsonString = await objectBlob.text(); // Convert Blob to text

    // Handle JSONL files (newline-delimited JSON)
    if (key.endsWith('.jsonl')) {
      const lines = jsonString
        .trim()
        .split('\n')
        .filter((line) => line.trim() !== '');
      const result: any[] = [];

      for (const line of lines) {
        try {
          result.push(JSON.parse(line));
        } catch (parseError) {
          console.warn(`Skipping invalid JSON line in ${key}:`, parseError);
        }
      }

      return result as unknown as T;
    }

    // Regular JSON file
    return JSON.parse(jsonString) as T;
  } catch (error) {
    console.error(`Error parsing JSON from S3 object ${key}:`, error);
    throw new Error(`Failed to parse JSON from S3: ${error instanceof Error ? error.message : String(error)}`);
  }
};

/**
 * Retrieves and parses a CSV file from S3 via the backend proxy.
 * @param key Key of the S3 object
 * @returns Array of objects representing CSV rows
 */
export const getCsvFromS3 = async (key: string): Promise<Record<string, string>[]> => {
  try {
    const objectBlob = await getS3Object(key);

    if (!objectBlob) {
      throw new Error('S3 object body is empty or not retrieved');
    }

    const csvString = await objectBlob.text(); // Convert Blob to text

    const lines = csvString.trim().split('\n');

    if (lines.length === 0) {
      return [];
    }

    // Extract headers (first line)
    const headers = lines[0].split(',').map((header: string) => header.trim());

    // Parse data rows
    const results: Record<string, string>[] = [];
    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',').map((value: string) => value.trim());
      const row: Record<string, string> = {};

      headers.forEach((header: string, index: number) => {
        if (index < values.length) {
          row[header] = values[index];
        } else {
          row[header] = '';
        }
      });

      results.push(row);
    }

    return results;
  } catch (error) {
    console.error(`Error parsing CSV from S3 object ${key}:`, error);
    throw new Error(`Failed to parse CSV from S3: ${error instanceof Error ? error.message : String(error)}`);
  }
};

/**
 * Retrieves a signed URL for an S3 object by calling the backend proxy.
 * The 'expiresIn' parameter is typically handled by the backend's configuration
 * or passed to the backend, but your current backend uses a fixed 1-hour expiry.
 * @param key Key of the S3 object
 * @returns Signed URL
 */
export const getSignedUrl = async (key: string): Promise<string> => {
  try {
    console.log(`Generating signed URL for ${key} via backend proxy`);

    const response = await fetch(`${BACKEND_BASE_URL}/signed-url`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ key }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
    }

    const data: { url: string } = await response.json();
    return data.url;
  } catch (error) {
    console.error(`Error generating signed URL for ${key}:`, error);
    throw new Error(`Failed to generate signed URL: ${error instanceof Error ? error.message : String(error)}`);
  }
};

/**
 * Function to parse TSV data
 * @param tsvData TSV data as a string
 * @returns Parsed data in JSON format
 */
export const parseTSV = (
  tsvData: string,
): { head: { vars: string[] }; results: { bindings: Record<string, { type: string; value: string }>[] } } => {
  if (!tsvData || tsvData.trim() === '') {
    return { head: { vars: [] }, results: { bindings: [] } };
  }

  const lines = tsvData.trim().split('\n');
  if (lines.length === 0) {
    return { head: { vars: [] }, results: { bindings: [] } };
  }

  // The first line contains variable names
  const vars = lines[0].split('\t');

  // Subsequent lines contain values
  const bindings: Record<string, { type: string; value: string }>[] = [];
  for (let i = 1; i < lines.length; i++) {
    const values = lines[i].split('\t');
    const binding: Record<string, { type: string; value: string }> = {};

    for (let j = 0; j < vars.length; j++) {
      if (j < values.length) {
        binding[vars[j]] = { type: 'literal', value: values[j] };
      }
    }

    bindings.push(binding);
  }

  return {
    head: { vars },
    results: { bindings },
  };
};

/**
 * Extracts the text content of an S3 object.
 * Since getS3Object now returns a Blob, this function converts the Blob to text.
 * @param s3ObjectBlob Blob returned by getS3Object
 * @returns The text content of the S3 object or an empty string if not available
 */
export const getS3ObjectText = async (s3ObjectBlob: Blob): Promise<string> => {
  try {
    if (!s3ObjectBlob) {
      return '';
    }
    return await s3ObjectBlob.text();
  } catch (error) {
    console.error('Error converting S3 object Blob to text:', error);
    return '';
  }
};

export default {
  listBuckets, // Added listBuckets to the default export
  listS3Objects,
  getS3Object,
  getJsonFromS3,
  getCsvFromS3,
  getSignedUrl,
  parseTSV,
  getS3ObjectText,
};
